import * as React from "react";
import { useState, useEffect } from "react";
import { Button, DatePicker, Form, Popover, Row, Select, Tooltip, Col, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { RootStateOrAny, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import moment from "moment";
import find from "lodash/find";
import { v4 as uuid } from "uuid";
import {
  toDisplayFormat,
  toDisplayFormatWithTime,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { checkIsLoaded, checkIsLoading, geDicValueById } from "../../lib/utils";
import { Account } from "../../lib/interfaces/Account";
import "./AccountListIndividual.less";
import { MENU_ACCOUNTS_INDIVIDUAL_WRITE, ACCOUNT_CREATE } from "../../lib/const/permissions";
import { hasAccess } from "../RolesModule/hasAccess";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as LoupeIcon } from "../../img/loupe-icon-currentColor.svg";
import { Subscriber } from "../../lib/interfaces/Subscriber";
import { useAppDispatch } from "../../index";
import { createAccountVersion } from "../../store/slices/accountsTs";
import SearchFiltersIndividual from "./SearchFiltersIndividual";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { IindividualSearchValues } from "../../lib/interfaces/IindividualSearchValues";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { Individual } from "../../lib/interfaces/Individual";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import featuresToggle from "../../lib/utils/featuresToggle";
import { hasLocalAccess } from "../../lib/configs";
import useBlockingMode from "../../lib/hooks/useBlockingMode";
import { getBlockingStatusIcon } from "../../lib/utils/getBlockingStatusIcon";
import BlockingModal from "../BlockingModal/BlockingModal";
import { BlockingObjTypes } from "../../lib/const/blockingObjTypes";

const { Option } = Select;

interface AccountVersionRow {
  key?: string;
  id: number;
  externalId: string[];
  phones: string[];
  fullName: string;
  fromDate: string;
  toDate: string;
  accountStatusId: number;
  isCurrentVersion: boolean;
  isPastVersion: boolean;
  isLatestVersion: boolean;
  isCreated: boolean;
  subscribers: Subscriber[];
  individual: Individual;
  accTypeId: number;
}

interface IAccountListIndividual {
  handlePageableSearch: (props: IindividualSearchValues) => void;
  getRowVersions: (params: {
    accountId: number;
    individualId: number;
    subscribersId: number[];
  }) => void;
  individualSearchValues: IindividualSearchValues;
  handleSetIndividualSearchValues: (prop: AnyObject) => void;
  individualSearchFullName: string;
  setIndividualSearchFullName: React.Dispatch<React.SetStateAction<string>>;
  initialIndividualSearchValues: IindividualSearchValues;
}

interface AccountTableRow {
  key: number;
  id: number;
  externalId: string[];
  phones: string[];
  fullName: string;
  fromDate: string;
  toDate: string;
  accountStatusId: number;
  individualId: number;
  subscribersId: number[];
  blockingStatus: number | null;
  accTypeId: number;
}

function AccountListIndividual(props: IAccountListIndividual) {
  const {
    handlePageableSearch,
    getRowVersions,
    individualSearchValues,
    handleSetIndividualSearchValues,
    individualSearchFullName,
    setIndividualSearchFullName,
    initialIndividualSearchValues,
  } = props;
  const {
    accountsIndividual,
    accountsIndividualLoading,
    individualVersions,
    individualVersionsLoading,
  } = useSelector((state: RootStateOrAny) => state.accountsTs);
  const { accountsVersions, accountsVersionsLoading } = useSelector(
    (state: RootStateOrAny) => state.accounts
  );
  const { accountStatuses, accountTypes } = useSelector((state: RootStateOrAny) => state.dicts);

  const { subscribersVersions, subscribersVersionsLoading } = useSelector(
    (state: RootStateOrAny) => state.accountsTs.accountListRowData
  );

  const isBlockingMode = useBlockingMode();

  const [searchIndividualForm] = Form.useForm();

  const [dataSource, setDataSource] = useState<AccountTableRow[]>([]);
  const [childDataSource, setChildDataSource] = useState<AccountVersionRow[]>([]);
  const [isDisabledButtons, setIsDisabledButtons] = useState(false);
  const [searchByNameForm] = Form.useForm();
  const [versionsTableForm] = Form.useForm();

  const { getFullPhoneToDisplay } = usePhoneMask();

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childDataSource);

  const dispatch = useAppDispatch();
  const history = useHistory();

  const showHistoryModal = (rec) => {
    setHistoryModalVisible(true);
    setObjectId(rec.id);
  };

  const [objectBlockingId, setObjectBlockingId] = useState<{ id?: number; fullName?: string }>({});

  const handleShowBlockingModal = (objId) => {
    setObjectBlockingId(objId);
  };

  const handleHideBlockingModal = () => {
    setObjectBlockingId({});
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    id: "100px",
    fullName: "15%",
    externalId: "15%",
    phones: "20%",
    accountStatusId: "",
    blockingIco: "50px",
    accTypeId: "10%",
  };

  const getColumns = () =>
    [
      {
        title: "ID аккаунта",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        hidden: true,
      },
      {
        title: "Аккаунт",
        dataIndex: "fullName",
        key: "fullName",
        width: columnsWidths.fullName,
        hidden: false,
      },
      {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: columnsWidths.blockingIco,
        hidden: false,
        render: (blockingStatus, record) => (
          <div>{getBlockingStatusIcon(blockingStatus, () => handleShowBlockingModal(record))}</div>
        ),
      },
      {
        title: "Тип аккаунта",
        dataIndex: "accTypeId",
        key: "accTypeId",
        width: columnsWidths.accTypeId,
        hidden: true,
        render: (accTypeId) => geDicValueById(accountTypes, accTypeId)?.name,
      },
      {
        title: "Технический ID",
        dataIndex: "externalId",
        key: "externalId",
        width: columnsWidths.externalId,
        hidden: false,
        render: (text, record) => {
          if (record.externalId?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {record.externalId.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${record.externalId[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return text;
        },
      },
      {
        title: "Контактный телефон",
        dataIndex: "phones",
        key: "phones",
        width: columnsWidths.phones,
        hidden: false,
        render: (phones) => {
          if (phones?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {phones.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${phones[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return phones?.[0] || "";
        },
      },
      {
        title: "Статус",
        dataIndex: "accountStatusId",
        key: "accountStatusId",
        width: columnsWidths.accountStatusId,
        hidden: false,
        render: (value) => accountStatuses.find((status) => status.id === value).name,
      },
      {
        title: "Период",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        hidden: false,
        render: (value, record) =>
          `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
      },
      {
        dataIndex: "actions",
        key: "actions",
        width: columnsWidths.actions,
        hidden: false,
      },
      {
        dataIndex: "buttons",
        key: "buttons",
        width: columnsWidths.buttons,
        className: "history-column",
        style: { display: "flex", justifyContent: "end" },
        hidden: false,
        render: (value, record) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        ),
      },
    ].filter((el) => {
      return !(el.key === "blockingStatus" && !isBlockingMode);
    });

  const [columns, setColumns] = useState(getColumns());

  useEffect(() => {
    setColumns((prev) => {
      const newColumns = getColumns().map((column, i) => {
        const prevColumnFind = prev.find((el) => el.key === column.key);

        return {
          ...column,
          hidden: prevColumnFind?.hidden !== undefined ? prevColumnFind.hidden : column.hidden,
        };
      });

      return newColumns;
    });
  }, [isBlockingMode]);

  const addVersion = (record: AccountVersionRow) => {
    setChildDataSource((prevState): AccountVersionRow[] =>
      [
        ...prevState,
        {
          ...record,
          key: uuid(),
          isCurrentVersion: false,
          isPastVersion: false,
          isLatestVersion: false,
          isCreated: true,
        },
      ].sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
    );
  };

  const getChildColumns = () =>
    [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        hidden: true,
      },
      {
        title: "Аккаунт",
        dataIndex: "fullName",
        key: "fullName",
        width: columnsWidths.fullName,
        hidden: false,
      },
      {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: columnsWidths.blockingIco,
        hidden: false,
        render: (blockingStatus) => <div>{getBlockingStatusIcon(undefined)}</div>,
      },
      {
        title: "Тип аккаунта",
        dataIndex: "accTypeId",
        key: "accTypeId",
        width: columnsWidths.accTypeId,
        hidden: true,
        render: (accTypeId) => geDicValueById(accountTypes, accTypeId)?.name,
      },
      {
        title: "Технический ID",
        dataIndex: "externalId",
        key: "externalId",
        width: columnsWidths.externalId,
        hidden: false,
        render: (text, record) => {
          if (record.externalId?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {record.externalId.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${record.externalId[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return text;
        },
      },
      {
        title: "Контактный телефон",
        dataIndex: "phones",
        key: "phones",
        width: columnsWidths.phones,
        hidden: false,
        render: (phones) => {
          if (phones?.length > 1) {
            return (
              <span
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popover
                  placement="topLeft"
                  content={() => (
                    <ul className="list-popover">
                      {phones.map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                >
                  <p style={{ margin: 0 }}>{`${phones[0].trim()},...`}</p>
                </Popover>
              </span>
            );
          }
          return phones?.[0] || "";
        },
      },
      {
        title: "Статус",
        dataIndex: "accountStatusId",
        key: "accountStatusId",
        width: columnsWidths.accountStatusId,
        hidden: false,
        render: (value, record) => {
          if (record.isCreated) {
            return (
              <Form.Item
                name="accountStatusId"
                rules={[{ required: true }]}
                className="form-item-required__hidden-error__mb0"
              >
                <Select style={{ width: "150px" }}>
                  {accountStatuses.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }
          return find(accountStatuses, (item) => item.id === value)?.name;
        },
      },
      {
        title: "Период",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        hidden: false,
        render: (value, record) => {
          if (record.isCreated) {
            return (
              <Row align="middle">
                <Form.Item
                  style={{ marginRight: "5px" }}
                  name="fromDate"
                  rules={[{ required: true }]}
                  className="form-item-required__hidden-error__mb0"
                >
                  <DatePicker
                    defaultPickerValue={moment(record.fromDate).add(1, "day")}
                    disabledDate={(current) =>
                      current <= moment(record.fromDate) || current > moment(record.toDate)
                    }
                  />
                </Form.Item>
                <span>- {toDisplayFormat(record.toDate)}</span>
              </Row>
            );
          }

          return `${toDisplayFormatWithTime(record.fromDate)} - ${toDisplayFormatWithTime(
            record.toDate
          )}`;
        },
      },
      {
        dataIndex: "actions",
        key: "actions",
        width: columnsWidths.actions,
        hidden: false,
        render: (value, record) => {
          if (hasAccess(MENU_ACCOUNTS_INDIVIDUAL_WRITE)) {
            if (!record.isPastVersion && !record.isCreated) {
              return (
                <Row justify="end">
                  <Tooltip title="Добавить версию">
                    <Button
                      className="iconButton"
                      icon={<AddVersionIcon />}
                      type="text"
                      htmlType="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDisabledButtons(true);
                        addVersion(record);
                      }}
                      disabled={isDisabledButtons}
                    />
                  </Tooltip>
                </Row>
              );
            }
            if (record.isCreated) {
              return (
                <Row>
                  <Tooltip title="Отменить">
                    <Button
                      className="iconButton"
                      icon={<CancelButtonIcon />}
                      type="text"
                      htmlType="button"
                      onClick={() => {
                        versionsTableForm.resetFields();
                        setIsDisabledButtons(false);
                        setChildDataSource((prevState) =>
                          prevState.filter((item) => !item.isCreated)
                        );
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Подтвердить">
                    <Button
                      className="iconButton"
                      icon={<SubmitButtonIcon />}
                      type="text"
                      htmlType="button"
                      onClick={versionsTableForm.submit}
                    />
                  </Tooltip>
                </Row>
              );
            }
          }
          return null;
        },
      },
      {
        dataIndex: "buttons",
        key: "buttons",
        width: columnsWidths.buttons,
        className: "history-column",
        style: { display: "flex", justifyContent: "end" },
        render: (v, rec) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(rec);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        ),
      },
    ].filter((el) => {
      return !(el.key === "blockingStatus" && !isBlockingMode);
    });

  const [
    childTableColumns,
    setChildTableColumns,
  ] = versionsTableUtils.useChildColumns(getChildColumns, [isChangingForbidden]);

  useEffect(() => {
    setChildTableColumns(getChildColumns());
  }, [isBlockingMode, accountTypes, isDisabledButtons]);

  const onFinish = () => {
    const newVersion = childDataSource.find((item) => item.isCreated);

    const { fromDate, accountStatusId } = versionsTableForm.getFieldsValue();
    if (newVersion) {
      const { id, individual, toDate, subscribers } = newVersion;
      dispatch(
        createAccountVersion({
          accountId: id,
          fromDate: toHttpFormatStartOfDay(fromDate),
          payload: {
            id,
            toDate,
            accountStatusId,
            individual,
            subscribers,
            accTypeId: newVersion.accTypeId,
          },
        })
      ).then(() => {
        getRowVersions({
          accountId: newVersion.id,
          individualId: newVersion.individual.id,
          subscribersId: newVersion.subscribers.map((item) => item.id),
        });
        setIsDisabledButtons(false);
      });
    }
  };

  useEffect(() => {
    const currentDate = moment();
    if (
      checkIsLoaded(accountsVersionsLoading, individualVersionsLoading, subscribersVersionsLoading)
    ) {
      setChildDataSource(
        accountsVersions[0]
          .map(
            (version, i): AccountVersionRow => {
              const versionIndividual = individualVersions.find((orgVersion) => {
                if (
                  moment(version.toDate).isSameOrAfter(orgVersion.fromDate) &&
                  moment(version.toDate).isSameOrBefore(orgVersion.toDate)
                ) {
                  return orgVersion;
                }
                return null;
              });
              const versionSubscription = subscribersVersions.flat().filter((item) => {
                if (
                  moment(version.toDate).isSameOrAfter(item.fromDate) &&
                  moment(version.toDate).isSameOrBefore(item.toDate)
                ) {
                  return item;
                }
                return null;
              });
              return {
                key: version.fromDate.concat(version.id),
                id: version.id,
                externalId: versionSubscription.map((item) => item.externalId),
                phones: versionIndividual?.phones.map(({ countryCode, phone }) =>
                  getFullPhoneToDisplay(countryCode, phone)
                ),
                fullName: [
                  versionIndividual?.lastName,
                  versionIndividual?.firstName,
                  versionIndividual?.middleName,
                ]
                  .filter(Boolean)
                  .join(" "),
                fromDate: version.fromDate,
                toDate: version.toDate,
                accountStatusId: version.accountStatusId,
                isCurrentVersion:
                  moment(currentDate).isSameOrAfter(version.fromDate) &&
                  moment(currentDate).isSameOrBefore(version.toDate),
                isPastVersion: moment(version?.toDate).isBefore(currentDate),
                isLatestVersion: accountsVersions[0].length === i + 1,
                isCreated: false,
                subscribers: versionSubscription,
                individual: versionIndividual,
                accTypeId: version.accTypeId,
              };
            }
          )
          .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
      );
    }
  }, [accountsVersionsLoading, individualVersionsLoading, subscribersVersionsLoading]);

  useEffect(() => {
    if (checkIsLoaded(accountsIndividualLoading)) {
      setDataSource(
        accountsIndividual.content.map(
          (acc: Account): AccountTableRow => ({
            key: acc.id,
            id: acc.id,
            externalId: acc.subscribers.map((item) => `${item.externalId} `),
            phones: acc.individual?.phones?.map(({ countryCode, phone }) =>
              getFullPhoneToDisplay(countryCode, phone)
            ),
            fullName: [
              acc.individual?.lastName,
              acc.individual?.firstName,
              acc.individual?.middleName,
            ]
              .filter(Boolean)
              .join(" "),
            fromDate: acc.fromDate,
            toDate: acc.toDate,
            accountStatusId: acc.accountStatusId,
            individualId: acc.individual?.id,
            subscribersId: acc.subscribers.map((item) => item.id),
            blockingStatus: acc?.blockingStatus,
            accTypeId: acc.accTypeId,
          })
        )
      );
    }
  }, [accountsIndividualLoading]);

  const resetSearchFullName = () => {
    setIndividualSearchFullName("");
    searchByNameForm.setFieldsValue({ fullName: "" });
  };

  const handleSearchByName = ({ fullName }: { fullName: string }): void => {
    handlePageableSearch({ fullName, page: 0 });
    setIndividualSearchFullName(fullName);
    handleSetIndividualSearchValues(initialIndividualSearchValues);
    searchIndividualForm.setFieldsValue(initialIndividualSearchValues);
  };

  const isCreateAccountAvailable = featuresToggle({
    value: true,
    alternativeValue: false,
    condition: hasLocalAccess("feature.createAccount"),
  });

  return (
    <div className="account-list">
      <HistoryAndPropertyModal
        objectTypeSysname="account"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ACCOUNTS_INDIVIDUAL_WRITE)}
      />
      <BlockingModal
        objectTypeId={BlockingObjTypes.Аккаунт}
        objectId={objectBlockingId?.id}
        handleHideModal={handleHideBlockingModal}
        visibleModal={!!objectBlockingId?.id}
        accountName={objectBlockingId?.fullName}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <h1 className="main-title">Список аккаунтов. Физические лица</h1>
        </Col>
        <Col>
          <Form form={searchByNameForm} onFinish={handleSearchByName}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const fullName = getFieldValue("fullName");
                const icon = fullName ? (
                  <Tooltip title="Найти">
                    <LoupeIcon
                      style={{
                        color: "#334455",
                        cursor: "pointer",
                      }}
                      onClick={searchByNameForm.submit}
                    />
                  </Tooltip>
                ) : (
                  <LoupeIcon color="#aab9c8ff" />
                );
                return (
                  <Form.Item
                    className="searchByNameInput"
                    name="fullName"
                    initialValue={individualSearchFullName}
                  >
                    <Input
                      placeholder="Поиск по наименованию"
                      suffix={icon}
                      allowClear
                      onChange={(e) => {
                        const { value } = e.target;
                        if (!value) {
                          resetSearchFullName();
                          handlePageableSearch(initialIndividualSearchValues);
                        }
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Row justify="end" style={{ marginBottom: 15, paddingRight: 10 }}>
        <Col>
          {hasAccess(ACCOUNT_CREATE) && isCreateAccountAvailable && (
            <Link to="/accounts/create-individual">
              <Button type="primary" icon={<PlusOutlined />}>
                Добавить аккаунт
              </Button>
            </Link>
          )}
        </Col>
      </Row>
      <SearchFiltersIndividual
        handlePageableSearch={handlePageableSearch}
        handleSetIndividualSearchValues={handleSetIndividualSearchValues}
        individualSearchValues={individualSearchValues}
        initialIndividualSearchValues={initialIndividualSearchValues}
        searchIndividualForm={searchIndividualForm}
        resetSearchFullName={resetSearchFullName}
      />
      <Form form={versionsTableForm} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="account-list-individual-table"
          columns={columns}
          setColumns={setColumns}
          tableData={dataSource}
          childTableColumns={childTableColumns}
          setChildTableColumns={setChildTableColumns}
          childTableData={childDataSource}
          isTableLoading={checkIsLoading(accountsIndividualLoading)}
          isChildTableLoading={checkIsLoading(
            accountsVersionsLoading,
            individualVersionsLoading,
            subscribersVersionsLoading
          )}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              getRowVersions({
                accountId: record.id,
                individualId: record.individualId,
                subscribersId: record.subscribersId,
              });
            }
          }}
          onCloseVersionsCallback={(record, expanded) => {
            if (expanded) {
              versionsTableForm.resetFields();
              setIsDisabledButtons(false);
              setChildDataSource((prevState) => prevState.filter((item) => !item.isCreated));
            }
          }}
          isPageable={{
            current: accountsIndividual.number + 1,
            total: accountsIndividual.totalElements,
            showSizeChanger: false,
            onChange: (page: number) => {
              let pageValue;
              pageValue = {
                page: page - 1,
              };

              if (individualSearchValues)
                pageValue = {
                  ...individualSearchValues,
                  page: page - 1,
                };

              if (individualSearchFullName) {
                pageValue = {
                  fullName: individualSearchFullName,
                  page: page - 1,
                };
              }
              handlePageableSearch(pageValue);
            },
          }}
          onRow={(record) => ({
            onClick: () => {
              history.push(`/accounts/${record.id}/info`);
            },
          })}
        />
      </Form>
    </div>
  );
}

export default AccountListIndividual;
